import { Component, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BoletoConfig } from '../../../core/models/payment.model';

/**
 * Presentational component that renders the Bradesco boleto returned by
 * GET /api/payments/boleto/orders/:orderId/config (and as gatewayData
 * from processPayment at order placement).
 *
 * Embedded in the order-details payment-box (compact mode) the same way
 * MultibancoDetailsComponent is. Boleto is render-only: the customer pays
 * at their bank and the order confirms asynchronously (reconciliation cron,
 * up to 3 business days) — so there is no completion event here.
 *
 * The visible line is `formattedDigitalLine` (with separators); the copy
 * button writes the raw 47-digit `digitalLine`, which is what banking
 * apps expect.
 */
@Component({
  selector: 'app-boleto-details',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule, TranslateModule],
  template: `
    <div class="boleto-details" [class.compact]="compact()">
      <div class="line-row">
        <span class="label">{{ 'PAYMENT_PROCESSING.BOLETO.DIGITAL_LINE' | translate }}</span>
        <div class="line-value">
          <span class="monospace">{{ config().formattedDigitalLine || config().digitalLine }}</span>
          <button type="button" class="copy-icon"
                  [matTooltip]="(copied() ? 'PAYMENT_PROCESSING.BOLETO.COPIED' : 'PAYMENT_PROCESSING.BOLETO.COPY_LINE') | translate"
                  [attr.aria-label]="copyAriaLabel()"
                  (click)="copyLine()">
            <mat-icon>{{ copied() ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        </div>
      </div>

      @if (config().url) {
        <a mat-stroked-button color="primary" class="open-btn"
           [href]="config().url" target="_blank" rel="noopener">
          <mat-icon>open_in_new</mat-icon>
          {{ 'PAYMENT_PROCESSING.BOLETO.OPEN_PDF' | translate }}
        </a>
      }

      <p class="note">{{ 'PAYMENT_PROCESSING.BOLETO.CONFIRMATION_NOTE' | translate }}</p>
    </div>
  `,
  styles: [`
    .boleto-details {
      background: #f5f5f5;
      border-radius: 8px;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .boleto-details.compact {
      background: transparent;
      padding: 0;
      gap: 8px;
      .label { font-size: 11px; }
      .monospace { font-size: 11px; }
      .note { font-size: 11px; }
      .copy-icon mat-icon {
        font-size: 14px;
        width: 14px;
        height: 14px;
      }
    }
    .line-row {
      .label {
        color: #666;
        font-size: 13px;
        display: block;
        margin-bottom: 4px;
      }
      .line-value {
        display: flex;
        align-items: center;
        gap: 6px;
      }
      .monospace {
        font-family: monospace;
        font-size: 12px;
        letter-spacing: 0.5px;
        word-break: break-all;
        font-weight: 500;
        flex: 1;
      }
    }
    .copy-icon {
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 2px;
      border-radius: 4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #666;
      flex-shrink: 0;
      &:hover { background: rgba(0, 0, 0, 0.06); color: #2e5339; }
      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }
    .open-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    }
    .note {
      color: #666;
      font-size: 12px;
      margin: 0;
    }
  `]
})
export class BoletoDetailsComponent {
  private translate = inject(TranslateService);

  config = input.required<BoletoConfig>();
  // When true, render compressed for embedding inside the order-details
  // payment-box. Same trade-off as MultibancoDetailsComponent's compact mode.
  compact = input<boolean>(false);

  copied = signal(false);

  copyAriaLabel(): string {
    return this.translate.instant(
      this.copied() ? 'PAYMENT_PROCESSING.BOLETO.COPIED' : 'PAYMENT_PROCESSING.BOLETO.COPY_LINE'
    );
  }

  async copyLine(): Promise<void> {
    // Raw digits — banking apps reject the formatted line's separators.
    const line = this.config().digitalLine;
    if (!line) return;
    try {
      await navigator.clipboard.writeText(line);
      this.copied.set(true);
      setTimeout(() => {
        if (this.copied()) this.copied.set(false);
      }, 2000);
    } catch {
      // Clipboard write can fail in non-secure contexts — silent no-op
    }
  }
}
